import type { AuthFailureReason, AuthRequestMode } from "../models/auth.model";
import { CompilationConfig, Stage } from "../models/config.model";
import { HostDimensions } from "../models/host-dimensions.model";

/**
 * Event ID constants as a const object (not enum) for zero-cost string-literal matching
 * and discriminated union compatibility.
 */
export const AppEventId = {
  // host → iframe events
  LOADER_READY: "loaderReady",
  URL: "url",
  IS_TEST_BOT: "isTestBot",
  IS_SMALL_SCREEN: "isSmallScreen",
  FOCUS_AFTER_IMAGE_CLOSED: "focusAfterImageClosed",
  STAGE: "stage",
  INIT: "init",
  AUTH_REQUEST: "auth_request",
  AUTH_RESULT: "auth_result",
  /** Carries the URL the host could not open (popup blocked). */
  OPEN_LINK_FAILED: "openLinkFailed",
  // iframe → host events
  INIT_ACK: "init_ack",
  IMAGE: "image",
  /** Carries the absolute URL of a link the host must open. */
  OPEN_LINK: "openLink",
  BOT_TOGGLED: "botToggled",
  // iframe -> host analytics events
  OPENED: "opened",
  CLOSED: "closed",
  USER_ENGAGED: "userEngaged",
  MESSAGE_SENT: "messageSent",
  MESSAGE_RECEIVED: "messageReceived",
  CLICK_LINK: "clickLink",
  REMOVE: "remove",
  UPDATE_HOST_DIMENSIONS: "updateHostDimensions",
  DRAG_START: "dragStart",
  DRAG_END: "dragEnd",
} as const;
export type AppEventId = (typeof AppEventId)[keyof typeof AppEventId];

/**
 * Structural host options, resolved once by the loader and immutable for the app instance.
 */
export interface HostSetupOptions {
  readonly entrypoint?: string;
  readonly extras: Readonly<Record<string, string>>;
}

/** Host setup options used when the loader supplies none. */
export const DEFAULT_HOST_SETUP_OPTIONS: HostSetupOptions = Object.freeze({
  extras: Object.freeze({}),
});

/** Correlated request sent when the application asks the loader to authenticate. */
export type AuthRequestEvent = {
  eventId: typeof AppEventId.AUTH_REQUEST;
  data: { requestId: string; mode: AuthRequestMode };
};

/** Correlated authentication success or failure returned by the loader. */
export type AuthResultEvent = {
  eventId: typeof AppEventId.AUTH_RESULT;
  data:
    | { requestId: string; outcome: "success"; sessionId: string }
    | {
        requestId: string;
        outcome: "failure";
        reason: AuthFailureReason;
        detail?: string;
      };
};

/**
 * Discriminated union of all typed event shapes sent between host and iframe.
 * The `eventId` field acts as the discriminant for TypeScript type narrowing.
 */
export type AppEvent =
  | { eventId: typeof AppEventId.LOADER_READY; data: null }
  | { eventId: typeof AppEventId.URL; data: string }
  | { eventId: typeof AppEventId.IS_TEST_BOT; data: boolean }
  | { eventId: typeof AppEventId.IS_SMALL_SCREEN; data: boolean }
  | { eventId: typeof AppEventId.FOCUS_AFTER_IMAGE_CLOSED; data: null }
  | { eventId: typeof AppEventId.STAGE; data: Stage }
  | {
      eventId: typeof AppEventId.INIT;
      data: CompilationConfig;
      hostSetup?: HostSetupOptions;
    }
  | AuthRequestEvent
  | AuthResultEvent
  | { eventId: typeof AppEventId.OPEN_LINK_FAILED; data: string }
  | { eventId: typeof AppEventId.INIT_ACK; data: null }
  | { eventId: typeof AppEventId.IMAGE; data: string }
  | { eventId: typeof AppEventId.OPEN_LINK; data: string }
  | { eventId: typeof AppEventId.BOT_TOGGLED; data: boolean }
  | { eventId: typeof AppEventId.OPENED; data: null }
  | { eventId: typeof AppEventId.CLOSED; data: null }
  | { eventId: typeof AppEventId.USER_ENGAGED; data: null }
  | { eventId: typeof AppEventId.MESSAGE_SENT; data: null }
  | { eventId: typeof AppEventId.MESSAGE_RECEIVED; data: null }
  | { eventId: typeof AppEventId.CLICK_LINK; data: string }
  | { eventId: typeof AppEventId.REMOVE; data: null }
  | { eventId: typeof AppEventId.UPDATE_HOST_DIMENSIONS; data: HostDimensions }
  | { eventId: typeof AppEventId.DRAG_START; data: null }
  | { eventId: typeof AppEventId.DRAG_END; data: null };

/**
 * Generic interface for dynamic local-storage response events.
 * These events are NOT part of the AppEvent union.
 * They flow through the event bus at runtime when local storage gets/sets occur.
 */
export interface LocalStorageEvent<T> {
  eventId: string;
  data: T;
}

export interface InitResult {
  port: MessagePort;
  compilationConfig: CompilationConfig;
  /** Structural host options received in the INIT handshake, defaulted when the loader sent none. */
  hostSetup: HostSetupOptions;
}
